import { IconButton, Typography } from '@material-tailwind/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiChevronLeft } from 'react-icons/bi'
import { useParams } from 'react-router-dom'
import illustration from '../assets/png/agami_experience.png'
import { useMapControls } from '../contexts/MapControlsContext'
import { useSessionContext } from '../contexts/SessionContext'
import { useMountEffect, usePageTitle } from '../hooks/toolkit'
import HeaderSpace from '../layout/HeaderSpace'
import { getUserExperiences } from '../utils/helpers/supabase.helpers'
import ExperienceCard from '../ViewExperience/ExperienceCard'
import SoundMap from './SoundMap'
import { AgamiUserExperience } from '../types/supabase'

const Experience = () => {
  const { t } = useTranslation()
  usePageTitle('Experience | Agami')

  const { experienceId } = useParams()
  const { user } = useSessionContext()
  const { drawer, setDrawer, drawerForced, setDrawerForced } = useMapControls()

  const [experiences, setExperiences] = useState<'loading' | AgamiUserExperience[] | null>('loading')

  const fetchExps = async () => {
    const userId = user?.id

    if (!userId) return

    const { data, error } = await getUserExperiences({ userId })

    if (error || !data?.length) {
      setExperiences(null)
      return
    }

    setExperiences(data)
  }

  useEffect(() => {
    if (experienceId) {
      setDrawerForced(false)
      setDrawer(false)
    } else {
      setDrawerForced(true)
    }
  }, [experienceId])

  useMountEffect(() => {
    fetchExps()
  })

  const showDrawer = drawerForced || drawer

  return (
    <div className="w-full h-full min-h-screen relative">
      <div
        className={`
          absolute z-40 h-full top-0 flex flex-col
          transition-all ease-in-out ${showDrawer ? 'w-full md:w-[70%] lg:w-[42%]' : 'w-0'}
          pt-8 bg-white overflow-x-hidden
          shadow-lg shadow-[#21323D60] dark:bg-primary-400
        `}
        data-testid="experience-drawer"
        data-open={showDrawer ? 'true' : 'false'}
      >
        <HeaderSpace />

        <div className={`w-full ${showDrawer ? 'px-6 md:px-12' : 'px-0'}`}>
          <div className="flex w-full justify-between items-start pb-4">
            <div className="flex flex-col">
              <Typography variant="h1" data-testid="explore-title">
                {t('experience.exploreTitle')}
              </Typography>
              {experiences !== 'loading' && experiences && experiences.length > 0 && (
                <Typography className="text-primary font-bold dark:text-white">
                  {t('experience.availableExperiences', { count: experiences.length })}
                </Typography>
              )}
            </div>

            {!drawerForced && drawer && (
              <IconButton
                className="text-primary dark:text-base-gray-500"
                onClick={() => setDrawer(false)}
                variant="text"
                color="gray"
                data-testid="experience-drawer-close-btn"
              >
                <BiChevronLeft className="h-6 w-6" />
              </IconButton>
            )}
          </div>

          {experiences === 'loading' && <Typography>{t('common.loading')}</Typography>}

          {experiences !== 'loading' && experiences && experiences.length === 0 && (
            <Typography>{t('experience.noExperiences')}</Typography>
          )}
        </div>

        <div
          className={`flex flex-col flex-1 overflow-y-auto dark:bg-primary-400 pb-8 ${showDrawer ? 'px-6 md:px-12' : 'px-0'}`}
          data-testid="experience-drawer-list"
        >
          {experiences !== 'loading' &&
            experiences &&
            experiences?.length > 0 &&
            experiences.map((exp) => (
              <ExperienceCard
                key={exp.experience_id}
                isActive={!!exp.experiences?.is_active}
                experienceId={exp.experience_id}
                name={exp.experiences?.name}
                owner={exp.experiences?.profiles}
                isLibrary={exp.experiences?.is_default && exp.experiences.owner_id === user?.id}
                isSelected={experienceId === exp.experience_id}
              />
            ))}
        </div>
      </div>

      <div className="md:block w-full h-full dark:bg-primary-400">
        {!experienceId ? (
          <div className="ml-auto md:w-[65%] pr-12 h-full flex flex-col items-center justify-center bg-white dark:bg-primary-400">
            <img src={illustration} className="w-[50%]" alt="Agami Experience" title="Agami Experience" />
          </div>
        ) : (
          <SoundMap experienceId={experienceId} />
        )}
      </div>
    </div>
  )
}

export default Experience
